import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import ReactDOM from 'react-dom'
import { AutoSizer, CellMeasurer, CellMeasurerCache, List } from 'react-virtualized'
import _ from 'lodash'
import { useNarrowWidth } from '../../hooks/useNarrowWidth'
import { cn } from '@appfluence/classnames'

const refsGetter = (compRef, listRef, innerRef) => comp => {
  compRef.current = comp

  if (_.isFunction(listRef)) {
    listRef(comp)
  }
  const div = ReactDOM.findDOMNode(comp)
  if (_.isFunction(innerRef)) {
    innerRef(div)
  }
}

export const VirtualizedList = ({
  rows,
  rowRenderer,
  noRowsRenderer,
  onRowsRendered,
  loading = false,
  placeholder,
  shimmersCount = 25,
  defaultHeight = 44,
  listRef,
  innerListRef,
  className,
}) => {
  const _listRef = useRef(null)
  const cacheRef = useRef(null)
  const widthRef = useRef(null)
  const currentRowsRef = useRef(null)
  const isMobileVersion = useNarrowWidth()
  currentRowsRef.current = rows
  if (!cacheRef.current) {
    cacheRef.current = new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight,
      keyMapper: rowIndex => {
        const e = currentRowsRef.current?.[rowIndex]
        return e?.hashCode?.() ?? e?.key ?? -1
      },
    })
  }

  const rowRendererWithCellMeasurer = useCallback(
    props => {
      return (
        <CellMeasurer cache={cacheRef.current} key={props.key} parent={props.parent} rowIndex={props.index}>
          {rowRenderer(props)}
        </CellMeasurer>
      )
    },
    [cacheRef, rowRenderer]
  )

  const noRowsRendererByDefault = useCallback(() => {
    return <div className="text-center text-pm-neutral-secondary">{placeholder}</div>
  }, [placeholder])

  useEffect(() => {
    _listRef.current?.recomputeRowHeights?.()
  }, [rows, cacheRef, _listRef])

  const noRowsRendererIfNeeded = useMemo(() => {
    if (placeholder) {
      return noRowsRendererByDefault
    }
    return noRowsRenderer
  }, [noRowsRenderer, noRowsRendererByDefault, placeholder])

  const rowsCount = loading ? rows.length + shimmersCount : rows.length
  return (
    <div className={cn('flex-1 overflow-hidden', className)}>
      <AutoSizer>
        {({ width, height }) => {
          if (width !== widthRef.current) {
            cacheRef.current.clearAll()
          }
          widthRef.current = width
          return (
            <List
              deferredMeasurementCache={cacheRef.current}
              width={width}
              height={height}
              rowCount={rowsCount}
              rowHeight={cacheRef.current.rowHeight}
              rowRenderer={rowRendererWithCellMeasurer}
              noRowsRenderer={noRowsRendererIfNeeded}
              onRowsRendered={onRowsRendered}
              overscanRowCount={20}
              ref={refsGetter(_listRef, listRef, innerListRef)}
              style={isMobileVersion ? { paddingBottom: '50px' } : {}}
            />
          )
        }}
      </AutoSizer>
    </div>
  )
}
